const VAR_REGEX = /{{(\w*)}}/g;
class I18NService<T> {
    private readonly _translations: T;
    private readonly _forceShowKeys: boolean;

    constructor(translations: T, forceShowKeys = false) {
        this._translations = translations;
        this._forceShowKeys = forceShowKeys;
    }

    public t = (acronym: keyof T, values = {}) => {
        /**
         * We use this flag to debug texts.
         */
        if (this._forceShowKeys) {
            return acronym;
        }

        /**
         * We handle pluralization in the same way as i18next.
         *
         * Right now, only singular / plural is supported. Multiple
         * plurals are not currently in scope.
         *
         * See [i18next](https://www.i18next.com/translation-function/plurals#singular-plural)
         * for more details
         */
        const isPlural = values && parseInt(values["count"] as string) > 1;
        let correctAcronym = acronym as string;
        if (isPlural && this._translations[`${acronym}_plural`]) {
            correctAcronym = `${acronym}_plural`;
        }

        if (!this._translations[correctAcronym]) {
            console.warn(`[i18n] Acronym not found. Acronym: ${acronym}`);
            return acronym;
        }

        return this._replaceValues(this._translations[correctAcronym], values);
    };

    /**
     * Interpolates texts using double brackets syntax. Variables that are
     * not provided inside values will be removed from the final string.
     * This is to keep compatibility with i18next.
     *
     * @example _replaceValues("You have {{AMOUNT}}€", {AMOUNT: 2}) // "You have 2€"
     */
    private _replaceValues(text: string, values: Record<string, string | number>) {
        if (!text) {
            return "";
        }

        let result = text;

        const matches = text.match(VAR_REGEX);
        if (!matches) {
            return result;
        }

        for (const match of matches) {
            const varName = match.replace("{{", "").replace("}}", "");
            result = result.replace(match, values[varName]?.toString() || "");
        }

        return result;
    }
}

export default I18NService;
